// CY-411: the printed picking list names, on every row, the prescription(s) the item is picked for, so the picker
// can tell which vial goes to which preparation. Rows refer to prescriptions by the picking list's own order ids
// ("1", "2"...); the order's rows map those to prescriptions, and the order's prescriptions give their numbers.

export interface PrintDistributedRow {
  orderId: string
  sequenceId: string
  returns?: readonly {returnId: string}[] | null
}

export interface PrintSequence {
  id: string
  internalId?: string | null
}

export interface PickingListPrescriptions {
  /** the prescription numbers of picking-list order ids, in picking-list order, without repeats */
  forOrderIds: (orderIds: readonly (string | null | undefined)[]) => string[]
  /** the prescription numbers a return is used for (the rows that list it), not the one it came back from */
  forReturn: (returnId: string) => string[]
}

export function getPickingListPrescriptions(
  rows: readonly PrintDistributedRow[],
  sequences: readonly PrintSequence[]
): PickingListPrescriptions {
  const numberBySequence = new Map(sequences.map(sequence => [sequence.id, sequence.internalId || sequence.id]))
  const position = new Map(rows.map((row, index) => [row.orderId, index]))
  const numberOf = (row: PrintDistributedRow) => numberBySequence.get(row.sequenceId) ?? row.sequenceId
  const rowByOrderId = new Map(rows.map(row => [row.orderId, row]))

  return {
    forOrderIds(orderIds) {
      const known = [...new Set(orderIds.filter((id): id is string => Boolean(id) && rowByOrderId.has(id!)))]
      known.sort((a, b) => (position.get(a) ?? 0) - (position.get(b) ?? 0))
      return [...new Set(known.map(id => numberOf(rowByOrderId.get(id)!)))]
    },
    forReturn(returnId) {
      return [...new Set(rows.filter(row => row.returns?.some(ret => ret.returnId === returnId)).map(numberOf))]
    },
  }
}
